import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getOutlets, getRegions, getCities, createOutlet, updateOutlet, deleteOutlet } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function OutletsPage() {
  const { t, language, isRTL } = useLanguage();
  const [outlets, setOutlets] = useState({ items: [], total: 0 });
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingOutlet, setEditingOutlet] = useState(null);
  const [deletingOutlet, setDeletingOutlet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 20;
  
  const [formData, setFormData] = useState({
    outlet_name_ar: '',
    outlet_name_en: '',
    outlet_category: '',
    project_info: '',
    region_id: '',
    city_id: '',
    district_id: '',
    latitude: '',
    longitude: '',
    icon_name: '',
    classification_number: '',
    status: 'draft',
    review_status: 'pending',
    is_sample: false,
    data_source: '',
    notes: '',
  });

  const filteredCities = cities.filter(c => c.region_id === formData.region_id);

  const fetchData = async () => {
    try {
      const [regionsRes, citiesRes] = await Promise.all([
        getRegions(),
        getCities()
      ]);
      setRegions(regionsRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      toast.error(t('error.general'));
    }
  };

  const fetchOutlets = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * pageSize,
        limit: pageSize
      };
      if (filterRegion !== 'all') params.region_id = filterRegion;
      
      const response = await getOutlets(params);
      setOutlets(response.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchOutlets();
  }, [page, filterRegion]);

  const getRegionName = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    return language === 'ar' ? region?.name_ar : (region?.name_en || region?.name_ar);
  };

  const handleOpenDialog = (outlet = null) => {
    if (outlet) {
      setEditingOutlet(outlet);
      setFormData({
        outlet_name_ar: outlet.outlet_name_ar || '',
        outlet_name_en: outlet.outlet_name_en || '',
        outlet_category: outlet.outlet_category || '',
        project_info: outlet.project_info || '',
        region_id: outlet.region_id || '',
        city_id: outlet.city_id || '',
        district_id: outlet.district_id || '',
        latitude: outlet.latitude || '',
        longitude: outlet.longitude || '',
        icon_name: outlet.icon_name || '',
        classification_number: outlet.classification_number || '',
        status: outlet.status || 'draft',
        review_status: outlet.review_status || 'pending',
        is_sample: outlet.is_sample || false,
        data_source: outlet.data_source || '',
        notes: outlet.notes || '',
      });
    } else {
      setEditingOutlet(null);
      setFormData({
        outlet_name_ar: '',
        outlet_name_en: '',
        outlet_category: '',
        project_info: '',
        region_id: '',
        city_id: '',
        district_id: '',
        latitude: '',
        longitude: '',
        icon_name: '',
        classification_number: '',
        status: 'draft',
        review_status: 'pending',
        is_sample: false,
        data_source: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.outlet_name_ar.trim() || !formData.region_id || !formData.city_id || 
        !formData.latitude || !formData.longitude) {
      toast.error(t('validation.required'));
      return;
    }
    
    const submitData = {
      ...formData,
      latitude: parseFloat(formData.latitude),
      longitude: parseFloat(formData.longitude),
      district_id: formData.district_id || null,
    };
    
    setSaving(true);
    try {
      if (editingOutlet) {
        await updateOutlet(editingOutlet.id, submitData);
        toast.success(t('success.updated'));
      } else {
        await createOutlet(submitData);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchOutlets();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingOutlet) return;
    
    try {
      await deleteOutlet(deletingOutlet.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingOutlet(null);
      fetchOutlets();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    }
  };

  const filteredOutlets = outlets.items?.filter(outlet => 
    outlet.outlet_name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    outlet.outlet_name_en?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const totalPages = Math.ceil(outlets.total / pageSize);

  return (
    <div className="space-y-4" data-testid="outlets-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full lg:w-auto">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="ps-10"
              data-testid="outlets-search"
            />
          </div>
          <Select value={filterRegion} onValueChange={(v) => { setFilterRegion(v); setPage(0); }}>
            <SelectTrigger className="w-full sm:w-40" data-testid="outlets-filter-region">
              <SelectValue placeholder={t('region')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')}</SelectItem>
              {regions.map((region) => (
                <SelectItem key={region.id} value={region.id}>
                  {language === 'ar' ? region.name_ar : (region.name_en || region.name_ar)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#0A4C3A] hover:bg-[#073B2D]"
          data-testid="add-outlet-button"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('add_outlet')}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-medium">{t('outlet_name_ar')}</TableHead>
                  <TableHead className="font-medium">{t('outlet_category')}</TableHead>
                  <TableHead className="font-medium">{t('region')}</TableHead>
                  <TableHead className="font-medium">{t('status')}</TableHead>
                  <TableHead className="font-medium text-center">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0A4C3A]" />
                    </TableCell>
                  </TableRow>
                ) : filteredOutlets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      {t('no_data')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOutlets.map((outlet) => (
                    <TableRow key={outlet.id} data-testid={`outlet-row-${outlet.id}`}>
                      <TableCell className="font-medium max-w-xs truncate">{outlet.outlet_name_ar}</TableCell>
                      <TableCell>{outlet.outlet_category || '-'}</TableCell>
                      <TableCell>{getRegionName(outlet.region_id)}</TableCell>
                      <TableCell>
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          outlet.status === 'published'
                            ? 'bg-green-100 text-green-800'
                            : outlet.status === 'archived'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {t(outlet.status)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(outlet)}
                            data-testid={`edit-outlet-${outlet.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setDeletingOutlet(outlet);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-outlet-${outlet.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <span className="text-sm text-gray-600">
                {t('total_rows')}: {outlets.total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </Button>
                <span className="text-sm text-gray-600">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOutlet ? t('edit_outlet') : t('add_outlet')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Names */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="outlet_name_ar">{t('outlet_name_ar')} *</Label>
                <Input
                  id="outlet_name_ar"
                  value={formData.outlet_name_ar}
                  onChange={(e) => setFormData({ ...formData, outlet_name_ar: e.target.value })}
                  data-testid="outlet-name-ar"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="outlet_name_en">{t('outlet_name_en')}</Label>
                <Input
                  id="outlet_name_en"
                  value={formData.outlet_name_en}
                  onChange={(e) => setFormData({ ...formData, outlet_name_en: e.target.value })}
                  dir="ltr"
                  data-testid="outlet-name-en"
                />
              </div>
            </div>

            {/* Category & Classification */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="outlet_category">{t('outlet_category')}</Label>
                <Input
                  id="outlet_category"
                  value={formData.outlet_category}
                  onChange={(e) => setFormData({ ...formData, outlet_category: e.target.value })}
                  data-testid="outlet-category"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="classification_number">{t('classification_number')}</Label>
                <Input
                  id="classification_number"
                  value={formData.classification_number}
                  onChange={(e) => setFormData({ ...formData, classification_number: e.target.value })}
                  dir="ltr"
                  data-testid="outlet-classification"
                />
              </div>
            </div>

            {/* Region & City */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('region')} *</Label>
                <Select 
                  value={formData.region_id} 
                  onValueChange={(value) => setFormData({ ...formData, region_id: value, city_id: '' })}
                >
                  <SelectTrigger data-testid="outlet-region-select">
                    <SelectValue placeholder={t('select_region')} />
                  </SelectTrigger>
                  <SelectContent>
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {language === 'ar' ? region.name_ar : (region.name_en || region.name_ar)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('city')} *</Label>
                <Select 
                  value={formData.city_id} 
                  onValueChange={(value) => setFormData({ ...formData, city_id: value })}
                  disabled={!formData.region_id}
                >
                  <SelectTrigger data-testid="outlet-city-select">
                    <SelectValue placeholder={t('select_city')} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>
                        {language === 'ar' ? city.name_ar : (city.name_en || city.name_ar)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Coordinates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="latitude">{t('latitude')} *</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  dir="ltr"
                  placeholder="24.7136"
                  data-testid="outlet-latitude"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">{t('longitude')} *</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  dir="ltr"
                  placeholder="46.6753"
                  data-testid="outlet-longitude"
                />
              </div>
            </div>

            {/* Status */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('status')}</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="outlet-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t('draft')}</SelectItem>
                    <SelectItem value="published">{t('published')}</SelectItem>
                    <SelectItem value="archived">{t('archived')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('review_status')}</Label>
                <Select 
                  value={formData.review_status} 
                  onValueChange={(value) => setFormData({ ...formData, review_status: value })}
                >
                  <SelectTrigger data-testid="outlet-review-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">{t('pending')}</SelectItem>
                    <SelectItem value="approved">{t('approved')}</SelectItem>
                    <SelectItem value="rejected">{t('rejected')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('notes')}</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="outlet-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-outlet-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingOutlet?.outlet_name_ar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-outlet"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
